import { Injectable } from '@angular/core';
import { loginSuccess, logout } from '../../../modules/auth/state/auth.actions';
import { take } from 'rxjs/operators';
import { clearLocationTree } from '../../../modules/location-tree/state/location-tree.actions';
import { FeUtil, BeUtil } from '../../../hezky-routes';
import { selectLoginOrigin } from '../../../state/app.state';
import { clearAppState, saveLoginOrigin } from '../../../state/app.actions';
import { getDetails, getToken } from '../../../modules/auth/state/auth.state';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  constructor(private feUtil: FeUtil, private beUtil: BeUtil) { }

  /**
   * Helper method to delete a cookie across all browsers
   * @param name Cookie name
   */
  private deleteCookie(name: string): void {
    const isLocalhost = window.location.origin.includes('localhost');
    const pastDate = new Date(0).toUTCString();
    if (isLocalhost) {
      document.cookie = `${name}=; path=/; expires=${pastDate}`;
    } else {
      document.cookie = `${name}=; path=/; domain=.hezkytech.com; expires=${pastDate}; secure; samesite=strict`;
      document.cookie = `${name}=; path=/; expires=${pastDate}`;
    }
  }
  
  /**
   * Helper method to get cookie value by name
   * @param name Cookie name
   * @returns Cookie value or undefined if not found
   */
  private getCookieValue(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
  }
  
  /**
   * Checks if the user is authenticated by verifying auth data in localStorage or cookies.
   * @returns {boolean} True if user is authenticated, false otherwise.
   */
  public isAuthenticated(): boolean {
    const authOrigin = this.getCookieValue('auth_origin');
    const auth = this.getCookieValue('auth');
    const authRedirectOrigin = this.getCookieValue('auth_redirect_origin');
    const hasLocalStorageAuth = !!localStorage.getItem('auth');
    
    // No authentication anywhere
    if (!authOrigin && !auth && !authRedirectOrigin && !hasLocalStorageAuth) {
      console.log('[AuthorizationService] No authentication found in cookies or localStorage.');
      return false;
    }

    // Handle cookie-based authentication for redirect origin
    if (authRedirectOrigin === window.location.origin) {
      if (!auth || !authOrigin) {
        console.warn('[AuthorizationService] Missing auth or authOrigin cookie, performing logout.');
        this.performLogout();
        return false;
      }
      try {
        const user = JSON.parse(decodeURIComponent(auth));
        if (!user) {
          console.warn('[AuthorizationService] User not found in auth cookie, redirecting to login.');
          window.location.href = `${authOrigin}/#/auth/login`;
          return false;
        }
        console.log('[AuthorizationService] Parsed user from cookie');
        this.feUtil.store.dispatch(saveLoginOrigin({ origin: authOrigin || window.location.origin }));

        let loginPayload: any = {};
        if (
          typeof user === 'object' &&
          user.details &&
          user.token &&
          user.refreshToken
        ) {
          loginPayload = {
            details: user.details,
            token: user.token,
            refreshToken: user.refreshToken
          };
        } else {
          loginPayload = {
            details: user,
            token: user.token,
            refreshToken: user.refreshToken
          };
        }
        this.feUtil.store.dispatch(loginSuccess(loginPayload));
        localStorage.setItem('INIT', 'true');
        console.log('[AuthorizationService] User authenticated and state patched');
        return true;
      } catch (error) {
        console.error('[AuthorizationService] Error parsing authentication cookie:', error);
        return false;
      }
    }

    // Check auth state from store synchronously
    let isAuthenticatedState = false;
    this.feUtil.store.select(getDetails).pipe(take(1)).subscribe(details => {
      this.feUtil.store.select(getToken).pipe(take(1)).subscribe(token => {
        isAuthenticatedState = !!details || !!token;
      });
    });

    if (!isAuthenticatedState) {
      console.log('[AuthorizationService] User not authenticated via auth state.');
      return false;
    }
    return true;
  }
  
  /**
   * Handles user logout functionality
   * - Broadcasts logout message to other tabs
   * - Clears application state and storage
   * - Removes auth cookies
   * - Redirects to login page
   * - Logs the logout event
   */
  public onLogout(onComplete?: () => void): void {
    const finalizeLogout = () => {
      onComplete?.();
      this.performLogout();
    };
    this.beUtil.authSvc.logout()
      .pipe(take(1))
      .subscribe({
        next: ({ status }) => {
          if (status !== 200) {
            this.feUtil.message.error('Failed to make standard logout!');
            onComplete?.();
            return;
          }
          finalizeLogout();
        },
        error: () => finalizeLogout()
      });
  }

  /**
   * Clears NgRx store state for logout. Single place for store reset (DRY).
   * Used by this service on logout and by TabManagerService when other tabs receive logout broadcast.
   */
  public clearStoreState(): void {
    this.feUtil.store.dispatch(clearAppState());
    this.feUtil.store.dispatch(logout());
    this.feUtil.store.dispatch(clearLocationTree());
  }

  /**
   * Performs the actual logout process by:
   * - Broadcasting logout message to other tabs
   * - Redirecting to login page based on origin
   * - Clearing application state and storage
   * - Removing authentication cookies
   * - Displaying success message
   * @private
   */
  private performLogout(): void {
    // Clear localStorage and sessionStorage immediately in this tab
    localStorage.clear();
    sessionStorage.clear();
    
    // Broadcast logout event to all tabs
    const broadcastChannel = new BroadcastChannel('tab_management');
    try {
      broadcastChannel.postMessage({ type: 'logout' });
    } finally {
      broadcastChannel.close();
    }

    // Helper: Clear all app state and show message
    const finalizeLogout = (): void => {
      this.feUtil.modalSvc.closeAll();
      this.clearStoreState();
      this.feUtil.message.success('You have successfully logged out.', { nzDuration: 2000 });
    };

    // Helper: Redirect to login page
    const redirectToLogin = (origin?: string): void => {
      if (origin && this.getCookieValue('auth_redirect_origin') === window.location.origin) {
        window.location.href = `${origin}/#/auth/login`;
        this.deleteCookie('auth_redirect_origin');
        this.deleteCookie('auth');
        this.deleteCookie('auth_origin');
      } else {
        this.feUtil.router.navigate(['/auth/login']);
      }
    };

    // Get login origin and handle redirect
    this.feUtil.store.select(selectLoginOrigin).pipe(take(1)).subscribe({
      next: (origin: string | null) => {
        redirectToLogin(origin ?? undefined);
      },
      error: (err: unknown) => {
        console.error('Error selecting login origin:', err);
        redirectToLogin();
      },
      complete: finalizeLogout
    });
  }
}